import { createContext, type ReactNode, useContext } from 'react';
import type { MeMembership, MeTenant } from '@/lib/types';
import { useSession } from '@/stores/session';

const MembershipContext = createContext<MeMembership | null>(null);

/** Provided by the `_app` layout once /v1/me is loaded and a tenant is selected. */
export function MembershipProvider({ value, children }: { value: MeMembership; children: ReactNode }) {
  return <MembershipContext.Provider value={value}>{children}</MembershipContext.Provider>;
}

/** The signed-in member's membership in the active tenant (roles, permissions, branches, modules, terminology). */
export function useMembership(): MeMembership {
  const m = useContext(MembershipContext);
  if (!m) throw new Error('useMembership() must be used inside the authenticated app layout (_app)');
  return m;
}

/** Same as `useMembership()` but returns null outside the `_app` layout (e.g. in shared components). */
export function useOptionalMembership(): MeMembership | null {
  return useContext(MembershipContext);
}

/** Active tenant (id, name, timezone, currency, locale, uiMode, ...). Only inside `_app`. */
export function useTenant(): MeTenant {
  return useMembership().tenant;
}

/** Tenant id for query keys: `[tenantId, 'customers', params]` (docs/04 §7). */
export function useTenantId(): string {
  return useMembership().tenant.id;
}

/** Choose the active membership: the stored tenant if still valid, else the only one, else none. */
export function pickActiveMembership(
  memberships: readonly MeMembership[],
  storedTenantId: string | null,
): MeMembership | null {
  const stored = storedTenantId ? memberships.find((m) => m.tenant.id === storedTenantId) : undefined;
  if (stored) return stored;
  if (memberships.length === 1) return memberships[0] ?? null;
  return null;
}

/**
 * Stored tenant id to keep once /me is known: unchanged when it is one of the user's shops, else the first shop,
 * else null (new account → onboarding must not send a previous account's shop id). Nothing stored stays null so a
 * multi-shop user still picks a shop.
 */
export function reconcileTenantId(
  memberships: readonly MeMembership[],
  storedTenantId: string | null,
): string | null {
  if (!storedTenantId || memberships.some((m) => m.tenant.id === storedTenantId)) return storedTenantId;
  return memberships[0]?.tenant.id ?? null;
}

/** simple|full: the user's local override wins over the tenant default (docs/08 §8). */
export function useUiMode(): 'simple' | 'full' {
  const tenant = useTenant();
  const override = useSession((s) => s.uiModeOverride);
  return override ?? tenant.uiMode;
}
